import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SearchIcon from '@mui/icons-material/Search';
import PuffLoader from 'react-spinners/PuffLoader';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AutocompleteSelectFilter from '../Widgets/AutocompleteSelectFilter/AutocompleteSelectFilter.jsx';
import { AmountInput } from '../ReportingFollowUp/SubComponents/SectionParts';
import { getMonthName } from '../ReportingFollowUp/reportingFollowUpConstants';
import NationalInsuranceService, { NATIONAL_INSURANCE_SAVED_EVENT } from '../../Services/nationalInsurance.service';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { formatDate_DD_MM_YYYY } from '../../Utils/FormatDate';
import followUpStyles from '../ReportingFollowUp/ReportingFollowUp.module.scss';
import styles from './NationalInsurance.module.scss';

const VALUE_FIELDS = ['monthlyAdvance', 'selfEmployedIncome', 'salariedIncome', 'nonWorkIncome'];
const FIELD_LABEL_KEYS = {
    monthlyAdvance: 'NIMonthlyAdvance',
    selfEmployedIncome: 'NISelfEmployedIncome',
    salariedIncome: 'NISalariedIncome',
    nonWorkIncome: 'NINonWorkIncome',
};
const SCOPE_CLIENT = 'client';
const SCOPE_ALL = 'all';
const ACCEPTED_FILES = '.htm,.html,.xla';
const FILTER_ALL = 'all';
const FILTER_MATCHED = 'matched';
const FILTER_UNMATCHED = 'unmatched';
const MATCH_MANUAL = 'manual';
const MATCH_REMEMBERED = 'remembered';
const PAGE_SIZE = 100;

const toInput = (value) => (value === null || value === undefined ? '' : String(value));
const toAmount = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(String(value).replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
};
const valuesToInputs = (values = {}) => Object.fromEntries(VALUE_FIELDS.map((f) => [f, toInput(values?.[f])]));
const inputsToValues = (inputs = {}) => Object.fromEntries(VALUE_FIELDS.map((f) => [f, toAmount(inputs[f])]));
const sameInputs = (a, b) => VALUE_FIELDS.every((f) => toAmount(a?.[f]) === toAmount(b?.[f]));
/** Comparable form of the editor state; months opened for editing but equal to the yearly values don't count. */
const snapshotOf = (yearInputs, monthInputs) => JSON.stringify({
    year: inputsToValues(yearInputs),
    months: Object.fromEntries(
        Object.entries(monthInputs)
            .filter(([, inputs]) => !sameInputs(inputs, yearInputs))
            .map(([month, inputs]) => [month, inputsToValues(inputs)])
    ),
});
const formatAmount = (value) => (value === null || value === undefined ? '—' : beautifyNumber(value, 0, true));
const formatDate = (value) => (value ? formatDate_DD_MM_YYYY(value).replace(/-/g, '/') : '');
const insuredName = (row) => [row.lastName, row.firstName].filter(Boolean).join(' ');
const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '').replace(/^0+/, '');
const nameTokens = (value) => String(value ?? '').split(/[\s\-'"׳״.,]+/).filter((token) => token.length >= 2);

/** Matched and unmatched preview rows in one list, each carrying its (possibly manual) client assignment. */
const buildImportRows = (preview, suggestFor) => {
    const suggestTokens = nameTokens(suggestFor?.name);
    const isSuggested = (row) => suggestTokens.length > 0
        && nameTokens(insuredName(row)).some((token) => suggestTokens.includes(token));

    const matched = (preview?.rows || []).map((row) => ({
        ...row,
        caseId: row.matchedCaseId,
        matchType: row.matchType || 'id',
        suggested: false,
    }));
    const unmatched = (preview?.unmatchedRows || []).map((row) => ({
        ...row,
        caseId: null,
        clientName: null,
        hasExistingData: false,
        matchType: null,
        suggested: isSuggested(row),
    }));
    unmatched.sort((a, b) => Number(b.suggested) - Number(a.suggested));

    return [...matched, ...unmatched].map((row, index) => ({ ...row, key: `${index}|${row.idNumber}` }));
};

const NationalInsuranceModal = ({ open, onClose, client, user, token, defaultYear, onSaved }) => {
    const { t, i18n } = useTranslation();
    const dir = i18n.dir();
    const language = i18n.language;
    const caseId = client?.caseId;
    const accountantId = user?.accountantId || client?.accountantId;

    const [year, setYear] = useState(() => defaultYear || new Date().getFullYear());
    const [clientYear, setClientYear] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [yearInputs, setYearInputs] = useState(valuesToInputs());
    const [monthInputs, setMonthInputs] = useState({});
    const [initialSnapshot, setInitialSnapshot] = useState('');

    const [scope, setScope] = useState(SCOPE_CLIENT);
    const [preview, setPreview] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [committing, setCommitting] = useState(false);
    const [importRows, setImportRows] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState(() => new Set());
    const [rowFilter, setRowFilter] = useState(FILTER_ALL);
    const [rowSearch, setRowSearch] = useState('');
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    const fileInputRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!open) return;
        setYear(defaultYear || new Date().getFullYear());
        setScope(SCOPE_CLIENT);
        setPreview(null);
        // Only re-seed when the modal opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const applyClientYear = useCallback((data) => {
        const nextYear = valuesToInputs(data?.values);
        const nextMonths = Object.fromEntries(
            (data?.months || []).filter((m) => m.isOverridden).map((m) => [m.month, valuesToInputs(m)])
        );
        setClientYear(data);
        setYearInputs(nextYear);
        setMonthInputs(nextMonths);
        setInitialSnapshot(snapshotOf(nextYear, nextMonths));
    }, []);

    const load = useCallback(async () => {
        if (!caseId || !token) return;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        try {
            const data = await NationalInsuranceService.getClientYear({ caseId, year }, token);
            if (requestId === requestIdRef.current) applyClientYear(data);
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            console.error('Error loading National Insurance data:', error);
            toast.error(t('NILoadFailed'));
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [caseId, token, year, applyClientYear, t]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const yearOptions = useMemo(() => {
        const current = new Date().getFullYear();
        return Array.from({ length: 8 }, (_, i) => current + 1 - i).map((y) => ({ value: y, label: String(y) }));
    }, []);

    const isDirty = initialSnapshot !== '' && initialSnapshot !== snapshotOf(yearInputs, monthInputs);
    const startMonth = clientYear?.startMonth ?? null;
    const isMonthActive = (month) => startMonth !== null && month >= startMonth;
    const columnTotals = useMemo(() => Object.fromEntries(VALUE_FIELDS.map((field) => {
        let sum = 0;
        let hasValue = false;
        for (let month = 1; month <= 12; month += 1) {
            if (startMonth === null || month < startMonth) continue;
            const amount = toAmount((monthInputs[month] || yearInputs)[field]);
            if (amount === null) continue;
            sum += amount;
            hasValue = true;
        }
        return [field, hasValue ? sum : null];
    })), [startMonth, monthInputs, yearInputs]);
    const setYearField = (field, value) => setYearInputs((prev) => ({ ...prev, [field]: value }));

    const setMonthField = (month, field, value) => {
        setMonthInputs((prev) => ({ ...prev, [month]: { ...(prev[month] || yearInputs), [field]: value } }));
    };

    const editMonth = (month) => {
        setMonthInputs((prev) => (prev[month] ? prev : { ...prev, [month]: { ...yearInputs } }));
    };

    const resetMonth = (month) => {
        setMonthInputs((prev) => {
            const updated = { ...prev };
            delete updated[month];
            return updated;
        });
    };

    const notifySaved = (caseIds) => {
        window.dispatchEvent(new CustomEvent(NATIONAL_INSURANCE_SAVED_EVENT, { detail: { caseIds } }));
        if (typeof onSaved === 'function') onSaved(caseIds);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const monthOverrides = Object.entries(monthInputs)
                .filter(([, inputs]) => !sameInputs(inputs, yearInputs))
                .map(([month, inputs]) => ({ month: Number(month), ...inputsToValues(inputs) }));
            const data = await NationalInsuranceService.saveClientYear({
                caseId,
                year,
                values: inputsToValues(yearInputs),
                monthOverrides,
            }, token);
            applyClientYear(data);
            toast.success(t('NISaved'));
            notifySaved([caseId]);
        } catch (error) {
            console.error('Error saving National Insurance data:', error);
            toast.error(t('NISaveFailed'));
        } finally {
            setSaving(false);
        }
    };

    // ------------------------------------------------------------------ import

    const isSingleScope = scope === SCOPE_CLIENT;

    const clearPreview = () => {
        setPreview(null);
        setImportRows([]);
        setSelectedKeys(new Set());
    };

    const handleFileSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setPreviewing(true);
        clearPreview();
        try {
            const data = await NationalInsuranceService.preview({
                accountantId,
                caseId: isSingleScope ? caseId : null,
                file,
            }, token);
            const rows = buildImportRows(data, isSingleScope ? client : null);
            const hasMatches = rows.some((row) => row.caseId);
            setPreview(data);
            setImportRows(rows);
            setSelectedKeys(new Set(rows.filter((row) => row.caseId).map((row) => row.key)));
            setRowFilter(isSingleScope && hasMatches ? FILTER_MATCHED : FILTER_ALL);
            setRowSearch('');
            setVisibleCount(PAGE_SIZE);
        } catch (error) {
            console.error('Error reading National Insurance file:', error);
            toast.error(error?.message ? `${t('NIImportFailed')}: ${error.message}` : t('NIImportFailed'));
        } finally {
            setPreviewing(false);
        }
    };

    const clientOptions = useMemo(() => (preview?.clients || []).map((c) => ({
        value: c.caseId,
        label: `${c.name || ''} · ${c.caseId}`,
        name: c.name,
        hasExistingData: c.hasExistingData,
    })), [preview]);
    const clientOptionsByCaseId = useMemo(
        () => new Map(clientOptions.map((option) => [option.value, option])),
        [clientOptions]
    );

    const assignRow = (rowKey, caseIdToAssign) => {
        const option = caseIdToAssign ? clientOptionsByCaseId.get(caseIdToAssign) : null;
        const previousHolder = option
            ? importRows.find((row) => row.key !== rowKey && row.caseId === option.value)
            : null;

        setImportRows((prev) => prev.map((row) => {
            if (row.key === rowKey) {
                return option
                    ? { ...row, caseId: option.value, clientName: option.name, hasExistingData: option.hasExistingData, matchType: MATCH_MANUAL }
                    : { ...row, caseId: null, clientName: null, hasExistingData: false, matchType: null };
            }
            if (previousHolder && row.key === previousHolder.key) {
                return { ...row, caseId: null, clientName: null, hasExistingData: false, matchType: null };
            }
            return row;
        }));
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (option) next.add(rowKey);
            else next.delete(rowKey);
            if (previousHolder) next.delete(previousHolder.key);
            return next;
        });
        if (previousHolder) {
            toast.info(t('NIMatchMoved', { idNumber: previousHolder.idNumber }));
        }
    };

    const matchedCount = importRows.filter((row) => row.caseId).length;
    const unmatchedCount = importRows.length - matchedCount;
    const manualCount = importRows.filter((row) => row.matchType === MATCH_MANUAL).length;

    const filteredRows = useMemo(() => {
        const term = rowSearch.trim().toLowerCase();
        const termDigits = digitsOnly(term);
        return importRows.filter((row) => {
            if (rowFilter === FILTER_MATCHED && !row.caseId) return false;
            if (rowFilter === FILTER_UNMATCHED && row.caseId) return false;
            if (!term) return true;
            if (termDigits && (digitsOnly(row.idNumber).includes(termDigits) || digitsOnly(row.caseId).includes(termDigits))) {
                return true;
            }
            return insuredName(row).toLowerCase().includes(term)
                || String(row.clientName || '').toLowerCase().includes(term);
        });
    }, [importRows, rowFilter, rowSearch]);
    const visibleRows = filteredRows.slice(0, visibleCount);

    const selectedRows = importRows.filter((row) => row.caseId && selectedKeys.has(row.key));
    const selectableVisible = filteredRows.filter((row) => row.caseId);
    const allSelected = selectableVisible.length > 0 && selectableVisible.every((row) => selectedKeys.has(row.key));
    const someSelected = selectableVisible.some((row) => selectedKeys.has(row.key));

    const toggleRow = (row) => {
        if (!row.caseId) return;
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(row.key)) next.delete(row.key);
            else next.add(row.key);
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            selectableVisible.forEach((row) => (allSelected ? next.delete(row.key) : next.add(row.key)));
            return next;
        });
    };

    const handleCommit = async () => {
        if (!preview || selectedRows.length === 0) return;
        setCommitting(true);
        try {
            const result = await NationalInsuranceService.commit({
                accountantId,
                year: preview.year,
                asOfDate: preview.asOfDate,
                officeName: preview.officeName,
                sourceFileName: preview.sourceFileName,
                rows: selectedRows.map((row) => ({
                    caseId: row.caseId,
                    idNumber: row.idNumber,
                    lastName: row.lastName,
                    firstName: row.firstName,
                    selfEmployedIncome: row.selfEmployedIncome,
                    salariedIncome: row.salariedIncome,
                    nonWorkIncome: row.nonWorkIncome,
                    monthlyAdvance: row.monthlyAdvance,
                })),
            }, token);
            toast.success(t('NIImportSuccess', { created: result?.created ?? 0, updated: result?.updated ?? 0 }));
            const importedYear = preview.year;
            clearPreview();
            notifySaved(selectedRows.map((row) => row.caseId));
            if (importedYear !== year) setYear(importedYear);
            else load();
        } catch (error) {
            console.error('Error importing National Insurance data:', error);
            toast.error(t('NISaveFailed'));
        } finally {
            setCommitting(false);
        }
    };

    // ------------------------------------------------------------------ render

    const renderClientCell = (row) => {
        if (isSingleScope) {
            if (!row.caseId) {
                return (
                    <Button
                        className={styles.assignButton}
                        size="small"
                        variant="outlined"
                        startIcon={<LinkIcon fontSize="small" />}
                        onClick={() => assignRow(row.key, caseId)}
                        disabled={committing}
                    >
                        {t('NIAssignToThisClient')}
                    </Button>
                );
            }
            return (
                <span className={styles.assignedClient}>
                    <span className={styles.assignedName}>{row.clientName} · {row.caseId}</span>
                    <Tooltip title={t('NIUnassign')}>
                        <IconButton className={styles.resetButton} size="small" onClick={() => assignRow(row.key, null)} disabled={committing}>
                            <LinkOffIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </span>
            );
        }

        return (
            <div className={styles.clientSelect}>
                <AutocompleteSelectFilter
                    options={clientOptions}
                    selectedValues={row.caseId ? clientOptionsByCaseId.get(row.caseId) || { value: row.caseId, label: `${row.clientName || ''} · ${row.caseId}` } : null}
                    onChange={(e, option) => assignRow(row.key, option?.value ?? null)}
                    placeholder={t('NIChooseClient')}
                    isMultiple={false}
                    isDisabled={committing}
                    direction={dir}
                    searchable
                    clearable
                    portalMenu
                />
            </div>
        );
    };

    const renderImportCard = () => (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <span className={styles.cardTitle}>{t('NIImportFile')}</span>
            </div>
            <div className={styles.cardBody}>
                <div className={styles.importBar}>
                    <ToggleButtonGroup
                        className={styles.scopeToggle}
                        size="small"
                        exclusive
                        value={scope}
                        onChange={(e, value) => {
                            if (!value) return;
                            setScope(value);
                            clearPreview();
                        }}
                        disabled={previewing || committing}
                    >
                        <ToggleButton value={SCOPE_CLIENT}>{t('NIImportScopeClient')}</ToggleButton>
                        <ToggleButton value={SCOPE_ALL}>{t('NIImportScopeAll')}</ToggleButton>
                    </ToggleButtonGroup>
                    <Button
                        className={styles.fileButton}
                        variant="contained"
                        disableElevation
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={previewing || committing || !accountantId}
                    >
                        {previewing ? t('Loading') : t('NIChooseFile')}
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPTED_FILES}
                        hidden
                        onChange={handleFileSelected}
                    />
                    <span className={styles.fileHint}>{t('NIFileHint')}</span>
                </div>

                {preview ? renderPreview() : null}
            </div>
        </div>
    );

    const renderPreview = () => (
        <>
            <div className={styles.chips}>
                {preview.officeName ? (
                    <span className={styles.chip}>{t('NIOfficeName')}: <strong>{preview.officeName}</strong></span>
                ) : null}
                {preview.asOfDate ? (
                    <span className={styles.chip}>{t('NIAsOfDate')}: <strong>{formatDate(preview.asOfDate)}</strong></span>
                ) : null}
                <span className={styles.chip}>{t('TaxYear')}: <strong>{preview.year}</strong></span>
                <span className={styles.chip}>{t('NIRowsInFile')}: <strong>{preview.totalRows}</strong></span>
                <span className={`${styles.chip} ${styles.chipSuccess}`}>
                    {t('NIMatchedRows')}: <strong>{matchedCount}</strong>
                </span>
                {manualCount > 0 ? (
                    <span className={`${styles.chip} ${styles.chipManual}`}>
                        {t('NIMatchManual')}: <strong>{manualCount}</strong>
                    </span>
                ) : null}
                <span className={`${styles.chip} ${styles.chipWarning}`}>
                    {t('NIUnmatchedRows')}: <strong>{unmatchedCount}</strong>
                </span>
            </div>

            {isSingleScope && matchedCount === 0 ? (
                <div className={styles.notice}>{t('NIClientNotInFileManual')}</div>
            ) : null}

            <div className={styles.rowsToolbar}>
                <ToggleButtonGroup
                    className={styles.scopeToggle}
                    size="small"
                    exclusive
                    value={rowFilter}
                    onChange={(e, value) => {
                        if (!value) return;
                        setRowFilter(value);
                        setVisibleCount(PAGE_SIZE);
                    }}
                >
                    <ToggleButton value={FILTER_ALL}>{t('NIFilterAll')} ({importRows.length})</ToggleButton>
                    <ToggleButton value={FILTER_MATCHED}>{t('NIFilterMatched')} ({matchedCount})</ToggleButton>
                    <ToggleButton value={FILTER_UNMATCHED}>{t('NIFilterUnmatched')} ({unmatchedCount})</ToggleButton>
                </ToggleButtonGroup>
                <label className={styles.searchField}>
                    <SearchIcon fontSize="small" />
                    <input
                        type="search"
                        value={rowSearch}
                        onChange={(e) => {
                            setRowSearch(e.target.value);
                            setVisibleCount(PAGE_SIZE);
                        }}
                        placeholder={t('NISearchRows')}
                        aria-label={t('NISearchRows')}
                    />
                </label>
            </div>

            {filteredRows.length === 0 ? (
                <div className={styles.emptyState}>{t('NINoRowsForFilter')}</div>
            ) : (
                <div className={`${styles.tableWrap} ${styles.tableWrapTall}`}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th className={styles.checkboxCell}>
                                    <Checkbox
                                        size="small"
                                        checked={allSelected}
                                        indeterminate={!allSelected && someSelected}
                                        onChange={toggleAll}
                                        disabled={selectableVisible.length === 0}
                                    />
                                </th>
                                <th>{t('NIIdNumber')}</th>
                                <th>{t('NIInsuredName')}</th>
                                <th className={styles.clientCell}>{t('NIClient')}</th>
                                {VALUE_FIELDS.map((field) => (
                                    <th key={field} className={styles.amountCell}>{t(FIELD_LABEL_KEYS[field])}</th>
                                ))}
                                <th aria-label={t('NIExistingWillUpdate')} />
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row) => (
                                <tr key={row.key} className={row.caseId ? undefined : styles.unmatchedRow}>
                                    <td className={styles.checkboxCell}>
                                        <Checkbox
                                            size="small"
                                            checked={!!row.caseId && selectedKeys.has(row.key)}
                                            onChange={() => toggleRow(row)}
                                            disabled={!row.caseId}
                                        />
                                    </td>
                                    <td dir="ltr">{row.idNumber}</td>
                                    <td>
                                        {insuredName(row)}
                                        {row.suggested && !row.caseId ? (
                                            <span className={`${styles.matchTag} ${styles.matchTagSuggested}`}>{t('NISuggested')}</span>
                                        ) : null}
                                    </td>
                                    <td className={styles.clientCell}>{renderClientCell(row)}</td>
                                    {VALUE_FIELDS.map((field) => (
                                        <td key={field} className={styles.amountCell}>{formatAmount(row[field])}</td>
                                    ))}
                                    <td>
                                        <div className={styles.tags}>
                                            {row.matchType === MATCH_MANUAL ? (
                                                <span className={`${styles.matchTag} ${styles.matchTagManual}`}>{t('NIMatchManual')}</span>
                                            ) : null}
                                            {row.matchType === MATCH_REMEMBERED ? (
                                                <span className={`${styles.matchTag} ${styles.matchTagManual}`}>{t('NIMatchRemembered')}</span>
                                            ) : null}
                                            {row.caseId && row.hasExistingData ? (
                                                <span className={styles.existingTag}>{t('NIExistingWillUpdate')}</span>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {filteredRows.length > visibleRows.length ? (
                <Button className={styles.linkButton} size="small" onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}>
                    {t('NIShowMore', { shown: visibleRows.length, total: filteredRows.length })}
                </Button>
            ) : null}

            <div className={styles.previewActions}>
                <Button
                    className={followUpStyles.cancel}
                    variant="contained"
                    disableElevation
                    onClick={clearPreview}
                    disabled={committing}
                >
                    {t('cancel')}
                </Button>
                <Button
                    className={followUpStyles.save}
                    variant="contained"
                    disableElevation
                    onClick={handleCommit}
                    disabled={committing || selectedRows.length === 0}
                >
                    {committing ? t('Loading') : `${t('NISaveImport')} (${selectedRows.length})`}
                </Button>
            </div>
        </>
    );

    const renderNotice = () => {
        if (!clientYear) return null;
        if (startMonth === null) {
            return <div className={styles.notice}>{t('NIEstablishedAfterYear')}</div>;
        }
        if (startMonth > 1 && clientYear.businessEstablishmentDate) {
            return (
                <div className={styles.notice}>
                    {t('NIStartsFromMonth', {
                        month: getMonthName(startMonth, language),
                        date: formatDate(clientYear.businessEstablishmentDate),
                    })}
                </div>
            );
        }
        if (!clientYear.hasData) {
            return <div className={styles.notice}>{t('NINoDataForYear')}</div>;
        }
        return null;
    };

    const renderMonthsCard = () => (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <span className={styles.cardTitle}>{t('NIMonthlyData')}</span>
                <div className={styles.meta}>
                    {clientYear?.asOfDate ? (
                        <span className={styles.chip}>{t('NIAsOfDate')}: <strong>{formatDate(clientYear.asOfDate)}</strong></span>
                    ) : null}
                    {clientYear?.sourceFileName ? (
                        <span className={styles.chip} title={clientYear.sourceFileName}>
                            {t('NISourceFile')}: <strong>{clientYear.sourceFileName}</strong>
                        </span>
                    ) : null}
                    {clientYear?.importedAt ? (
                        <span className={styles.chip}>{t('NIImportedAt')}: <strong>{formatDate(clientYear.importedAt)}</strong></span>
                    ) : null}
                    {clientYear?.isManuallyEdited ? (
                        <span className={`${styles.chip} ${styles.chipWarning}`}>{t('NIManuallyEdited')}</span>
                    ) : null}
                </div>
            </div>
            <div className={styles.cardBody}>
                {loading && !clientYear ? (
                    <div className={styles.loader}><PuffLoader size={60} loading /></div>
                ) : (
                    <div style={{ opacity: loading ? 0.5 : 1 }}>
                        {renderNotice()}
                        <div className={styles.monthsTable} style={{ marginTop: 10 }}>
                            <span className={styles.columnHead}>{t('NIMonth')}</span>
                            {VALUE_FIELDS.map((field) => (
                                <span key={field} className={styles.columnHead}>{t(FIELD_LABEL_KEYS[field])}</span>
                            ))}
                            <span />

                            <span className={styles.yearRowLabel}>{t('NIYearlyValues')}</span>
                            {VALUE_FIELDS.map((field) => (
                                <AmountInput
                                    key={field}
                                    value={yearInputs[field]}
                                    onChange={(value) => setYearField(field, value)}
                                    ariaLabel={`${t('NIYearlyValues')} ${t(FIELD_LABEL_KEYS[field])}`}
                                    total
                                />
                            ))}
                            <span />
                            <span className={styles.yearHint}>{t('NIYearlyAppliesHint')}</span>
                            <span className={styles.yearRowDivider} />

                            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                                const monthName = getMonthName(month, language);
                                if (!isMonthActive(month)) {
                                    return (
                                        <React.Fragment key={month}>
                                            <span className={`${styles.monthLabel} ${styles.inactiveLabel}`}>{monthName}</span>
                                            <span className={styles.inactiveRow}>{t('NIBeforeEstablishment')}</span>
                                        </React.Fragment>
                                    );
                                }
                                const editing = !!monthInputs[month];
                                if (!editing) {
                                    return (
                                        <React.Fragment key={month}>
                                            <span className={styles.monthLabel}>{monthName}</span>
                                            {VALUE_FIELDS.map((field) => (
                                                <span
                                                    key={field}
                                                    className={styles.inheritedValue}
                                                    title={t('NIFollowsYearly')}
                                                    aria-label={`${monthName} ${t(FIELD_LABEL_KEYS[field])}`}
                                                >
                                                    {formatAmount(toAmount(yearInputs[field]))}
                                                </span>
                                            ))}
                                            <Tooltip title={t('NIEditMonth')}>
                                                <IconButton className={styles.resetButton} size="small" onClick={() => editMonth(month)}>
                                                    <EditOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </React.Fragment>
                                    );
                                }
                                const changed = !sameInputs(monthInputs[month], yearInputs);
                                return (
                                    <React.Fragment key={month}>
                                        <span className={styles.monthLabel}>
                                            {changed ? (
                                                <Tooltip title={t('NIOverridden')}>
                                                    <span className={styles.overriddenDot} />
                                                </Tooltip>
                                            ) : null}
                                            {monthName}
                                        </span>
                                        {VALUE_FIELDS.map((field) => (
                                            <AmountInput
                                                key={field}
                                                value={monthInputs[month][field]}
                                                onChange={(value) => setMonthField(month, field, value)}
                                                ariaLabel={`${monthName} ${t(FIELD_LABEL_KEYS[field])}`}
                                            />
                                        ))}
                                        <Tooltip title={t('NIResetMonth')}>
                                            <IconButton className={styles.resetButton} size="small" onClick={() => resetMonth(month)}>
                                                <RestartAltIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </React.Fragment>
                                );
                            })}

                            <span className={styles.yearRowDivider} />
                            <span className={styles.totalRowLabel}>{t('NIYearTotal')}</span>
                            {VALUE_FIELDS.map((field) => (
                                <span
                                    key={field}
                                    className={styles.totalValue}
                                    aria-label={`${t('NIYearTotal')} ${t(FIELD_LABEL_KEYS[field])}`}
                                >
                                    {formatAmount(columnTotals[field])}
                                </span>
                            ))}
                            <span />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            dir={dir}
            PaperProps={{
                className: `${followUpStyles.paper} ${styles.paper}`,
                sx: { maxWidth: 1120, width: 'min(1120px, calc(100vw - 32px))' },
            }}
            BackdropProps={{ sx: { background: 'rgba(20, 30, 43, 0.45)', backdropFilter: 'blur(6px)' } }}
        >
            <DialogTitle className={followUpStyles.header}>
                <span>{t('NationalInsurance')}</span>
                <div className={followUpStyles.headerEnd}>
                    {client ? (
                        <span className={followUpStyles.headerClient}>{client.name} · {client.caseId}</span>
                    ) : null}
                    <div className={styles.yearField}>
                        <AutocompleteSelectFilter
                            options={yearOptions}
                            selectedValues={yearOptions.find((o) => o.value === year) || { value: year, label: String(year) }}
                            onChange={(e, option) => option && setYear(option.value)}
                            placeholder={t('TaxYear')}
                            isMultiple={false}
                            direction={dir}
                            portalMenu
                        />
                    </div>
                </div>
            </DialogTitle>

            <DialogContent className={followUpStyles.scroll}>
                <div className={styles.body}>
                    {renderImportCard()}
                    {renderMonthsCard()}
                </div>
            </DialogContent>

            <DialogActions className={followUpStyles.actions}>
                <Button className={followUpStyles.cancel} variant="contained" disableElevation onClick={onClose}>
                    {t('close')}
                </Button>
                <Button
                    className={followUpStyles.save}
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={!isDirty || saving || loading}
                >
                    {saving ? t('Loading') : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

NationalInsuranceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    client: PropTypes.object,
    user: PropTypes.object,
    token: PropTypes.string,
    defaultYear: PropTypes.number,
    onSaved: PropTypes.func,
};

export default NationalInsuranceModal;
